import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Cog, Send, Truck, CheckCircle2, XCircle, RotateCcw, Eye, Clock, Info, Pencil, ArrowRightLeft, Search, X,
} from "lucide-react";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { Badge, Badge as B } from "../components/Badges";
import { Modal } from "../components/Modal";
import { Loader } from "../components/PageHead";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtDateTime, fmtNum, money } from "../lib/format";
import { toast } from "sonner";

const PARTS_META = {
  requested: ["Requested", "b-amber"],
  required:  ["Requested", "b-amber"],  // legacy rows
  quoted: ["Quoted", "b-blue"],
  ordered: ["Ordered", "b-violet"],
  received: ["Received", "b-green"],
  not_available: ["Not Available", "b-red"],
  customer_declined: ["Customer Declined", "b-slate"],
  returned_to_technician: ["Returned to Tech", "b-slate"],
  moved_to_dispatch: ["Moved to Dispatch", "b-blue"],
  rtc: ["RTC — Returned to Customer", "b-red"],
};

// SPT-PTE-2026-0042: only 2 top-level tabs now — every request is either
// still open (needs a decision) or already closed out one of the 3 final
// ways. The granular quote/order/received sub-statuses still exist in the
// data model (status_history keeps full history) but are no longer surfaced
// as separate tabs or a mandatory gate to closing a request.
const TERMINAL_PARTS_STATUSES = ["returned_to_technician", "moved_to_dispatch", "rtc"];
const SECTIONS = [
  { key: "new_requests", label: "New Requests",
    match: (r) => !TERMINAL_PARTS_STATUSES.includes(r.status) },
  { key: "returned_to_technician", label: "Return to Tech",
    match: (r) => TERMINAL_PARTS_STATUSES.includes(r.status) },
];

// The 3 quick-close outcomes, available from ANY open part request —
// skips the quote → order → received sequence entirely when it isn't needed.
const QUICK_CLOSE_ACTIONS = [
  { to: "returned_to_technician", label: "Return to Tech", icon: RotateCcw,
    fields: [{ k: "notes", l: "Note for the technician", type: "textarea" }] },
  { to: "moved_to_dispatch", label: "Move to Dispatch", icon: Truck,
    fields: [{ k: "notes", l: "Note (optional)", type: "textarea" }] },
  { to: "rtc", label: "RTC — Return to Customer", icon: XCircle, tone: "danger",
    fields: [{ k: "notes", l: "Reason (required — no work will be done)", type: "textarea", required: true }] },
];

// Per-status step buttons + fields the parts/admin user needs to provide
const STATUS_STEPS = {
  requested:  [
    { to: "quoted", label: "Mark Quoted", icon: Cog,
      fields: [
        { k: "supplier", l: "Supplier", type: "text", required: true },
        { k: "quote_number", l: "Quote #", type: "text" },
        { k: "cost", l: "Cost (R, excl. VAT)", type: "number" },
        { k: "notes", l: "Notes", type: "textarea" },
      ] },
    { to: "ordered", label: "Mark Ordered", icon: Send,
      fields: [
        { k: "supplier", l: "Supplier", type: "text", required: true },
        { k: "purchase_order", l: "Purchase order #", type: "text", required: true },
        { k: "cost", l: "Cost (R, excl. VAT)", type: "number" },
        { k: "expected_eta", l: "Expected ETA", type: "date" },
        { k: "notes", l: "Notes", type: "textarea" },
      ] },
    { to: "not_available", label: "Not Available", icon: XCircle, tone: "danger",
      fields: [{ k: "notes", l: "Why is it unavailable?", type: "textarea", required: true }] },
    { to: "customer_declined", label: "Customer Declined", icon: XCircle, tone: "danger",
      fields: [{ k: "notes", l: "Customer's reason", type: "textarea", required: true }] },
  ],
  quoted: [
    { to: "ordered", label: "Mark Ordered", icon: Send,
      fields: [
        { k: "purchase_order", l: "Purchase order #", type: "text", required: true },
        { k: "expected_eta", l: "Expected ETA", type: "date" },
        { k: "notes", l: "Notes", type: "textarea" },
      ] },
    { to: "customer_declined", label: "Customer Declined", icon: XCircle, tone: "danger",
      fields: [{ k: "notes", l: "Customer's reason", type: "textarea", required: true }] },
    { to: "not_available", label: "Not Available", icon: XCircle, tone: "danger",
      fields: [{ k: "notes", l: "Why?", type: "textarea", required: true }] },
  ],
  ordered: [
    { to: "received", label: "Mark Received", icon: CheckCircle2, tone: "gold",
      fields: [{ k: "notes", l: "Condition / notes", type: "textarea" }] },
    { to: "not_available", label: "Order failed / Not Available", icon: XCircle, tone: "danger",
      fields: [{ k: "notes", l: "Why?", type: "textarea", required: true }] },
  ],
  received: [
    { to: "returned_to_technician", label: "Return to Tech", icon: RotateCcw,
      fields: [{ k: "notes", l: "Note for the technician", type: "textarea" }] },
  ],
  not_available: [
    { to: "returned_to_technician", label: "Close — back to tech", icon: RotateCcw,
      fields: [{ k: "notes", l: "Closing note", type: "textarea" }] },
  ],
  customer_declined: [
    { to: "returned_to_technician", label: "Close — back to tech", icon: RotateCcw,
      fields: [{ k: "notes", l: "Closing note", type: "textarea" }] },
  ],
};
STATUS_STEPS.required = STATUS_STEPS.requested;

function StepModal({ part, step, onClose, onDone }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    for (const f of step.fields || []) {
      if (!f.required) continue;
      const v = vals[f.k];
      if (!v || (typeof v === "string" && !v.trim())) {
        return toast.error(`${f.l} is required`);
      }
    }
    setBusy(true);
    try {
      const body = { status: step.to };
      (step.fields || []).forEach((f) => {
        let v = vals[f.k];
        if (f.type === "number") v = v === "" || v == null ? null : Number(v);
        if (v !== "" && v != null) body[f.k] = v;
      });
      await api.patch(`/parts/${part.id}`, body);
      toast.success(`${step.label} — saved`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const Icon = step.icon;
  return (
    <Modal open onClose={onClose} icon={Icon} title={step.label} subtitle={part.part_description}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className={`btn ${step.tone === "danger" ? "danger" : "gold"}`} onClick={submit} disabled={busy}
            data-testid={`parts-step-confirm-${step.to}`}>
            {busy ? "Saving…" : "Confirm"}
          </button>
        </>
      }>
      <div className="form-grid">
        {(step.fields || []).map((f) => (
          <div className="fld" key={f.k} style={{ gridColumn: f.type === "textarea" ? "span 2" : undefined }}>
            <label>{f.l} {f.required && <span className="req">*</span>}</label>
            {f.type === "textarea"
              ? <textarea className="input" rows={3} value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`parts-step-${f.k}`} />
              : <input className="input" type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`parts-step-${f.k}`} />}
          </div>
        ))}
      </div>
    </Modal>
  );
}

// SPT-PTE-2026-0036 — the document's own core fields (previously READ-ONLY,
// no matter the status) can now be edited directly. Independent of the
// status-transition steps above — always available.
function EditPartModal({ part, onClose, onDone }) {
  const [vals, setVals] = useState({
    part_description: part.part_description || "",
    part_number: part.part_number || "",
    quantity: part.quantity || 1,
    reason_required: part.reason_required || "",
    supplier_suggestion: part.supplier_suggestion || "",
    notes: part.notes || "",
    can_work_continue: !!part.can_work_continue,
    customer_approval_required: !!part.customer_approval_required,
    is_repair_item: !!part.is_repair_item,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!vals.part_description.trim()) return toast.error("Part description is required");
    const qty = Number(vals.quantity);
    if (!qty || qty < 1) return toast.error("Quantity must be at least 1");
    setBusy(true);
    try {
      await api.patch(`/parts/${part.id}/edit`, { ...vals, quantity: qty });
      toast.success("Document updated");
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Pencil} title="Edit spare parts document"
      subtitle={`${part.delivery_note_number || "—"} · ${part.customer_name || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy} data-testid="part-edit-submit">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </>}>
      <div className="form-grid">
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Part description <span className="req">*</span></label>
          <input className="input" value={vals.part_description}
            onChange={(e) => set("part_description", e.target.value)} data-testid="part-edit-description" />
        </div>
        <div className="fld"><label>Part number</label>
          <input className="input" value={vals.part_number}
            onChange={(e) => set("part_number", e.target.value)} data-testid="part-edit-part-number" /></div>
        <div className="fld"><label>Quantity</label>
          <input className="input" type="number" min={1} value={vals.quantity}
            onChange={(e) => set("quantity", e.target.value)} data-testid="part-edit-quantity" /></div>
        <div className="fld" style={{ gridColumn: "span 2" }}><label>Suggested supplier</label>
          <input className="input" value={vals.supplier_suggestion}
            onChange={(e) => set("supplier_suggestion", e.target.value)} data-testid="part-edit-supplier-suggestion" /></div>
        <div className="fld">
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 400, textTransform: "none" }}>
            <input type="checkbox" checked={vals.can_work_continue}
              onChange={(e) => set("can_work_continue", e.target.checked)} data-testid="part-edit-can-continue" />
            Work can continue without this part
          </label>
        </div>
        <div className="fld">
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 400, textTransform: "none" }}>
            <input type="checkbox" checked={vals.customer_approval_required}
              onChange={(e) => set("customer_approval_required", e.target.checked)} data-testid="part-edit-cust-approval" />
            Customer approval required
          </label>
        </div>
        <div className="fld">
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 400, textTransform: "none" }}>
            <input type="checkbox" checked={vals.is_repair_item}
              onChange={(e) => set("is_repair_item", e.target.checked)} data-testid="part-edit-repair-item" />
            This is a repair item
          </label>
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Reason required</label>
          <textarea className="input" rows={2} value={vals.reason_required}
            onChange={(e) => set("reason_required", e.target.value)} data-testid="part-edit-reason" />
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Notes</label>
          <textarea className="input" rows={2} value={vals.notes}
            onChange={(e) => set("notes", e.target.value)} data-testid="part-edit-notes" />
        </div>
      </div>
    </Modal>
  );
}

// SPT-PTE-2026-0036 — move this SAME parts request onto a different job's
// equipment line — needed when the originally-linked job has since been
// closed or scrapped but the spare part itself is still wanted elsewhere.
// Available regardless of the part's status or the current job's status.
function ReassignPartModal({ part, onClose, onDone }) {
  const [query, setQuery] = useState("");
  const [jobResults, setJobResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [openDrop, setOpenDrop] = useState(false);
  const [job, setJob] = useState(null);
  const [itemId, setItemId] = useState("");
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 1) { setJobResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/jobs", { params: { search: query, limit: 15 } });
        setJobResults(r.data.items || []);
      } catch { setJobResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenDrop(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pickJob = async (j) => {
    setOpenDrop(false); setQuery("");
    try {
      const r = await api.get(`/jobs/${j.id}`);
      setJob(r.data);
      setItemId("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submit = async () => {
    if (!itemId) return toast.error("Pick an equipment line to reassign to");
    setBusy(true);
    try {
      await api.patch(`/parts/${part.id}/edit`, { job_item_id: itemId });
      toast.success(`Reassigned to DN ${job.delivery_note_number || ""}`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={ArrowRightLeft} title="Reassign to a different job"
      subtitle={part.part_description}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy || !itemId} data-testid="part-reassign-submit">
          {busy ? "Reassigning…" : "Reassign"}
        </button>
      </>}>
      <div className="t-mut" style={{ fontSize: 12.5, marginBottom: 14 }}>
        Currently linked to DN <b className="mono">{part.delivery_note_number || "—"}</b>
        {part.customer_name ? ` · ${part.customer_name}` : ""}. Pick a different job below to move this
        request there — useful when the original job has been closed or scrapped.
      </div>
      <div className="fld" style={{ marginBottom: 14, position: "relative" }} ref={wrapRef}>
        <label>Job (Delivery note #) <span className="req">*</span></label>
        {job ? (
          <div className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid="part-reassign-job-selected">
            <span className="t-strong mono" style={{ fontSize: 13 }}>
              {job.delivery_note_number} <span className="t-mut" style={{ fontWeight: 400 }}>· {job.customer_name}</span>
            </span>
            <button type="button" className="icon-btn" style={{ width: 22, height: 22 }}
              onClick={() => { setJob(null); setItemId(""); }} data-testid="part-reassign-job-clear"><X size={13} /></button>
          </div>
        ) : (
          <>
            <div className="input" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Search size={13} color="var(--text-3)" />
              <input className="mono" style={{ border: "none", outline: "none", width: "100%", background: "none" }}
                placeholder="Search jobs by DN or customer…" value={query}
                onChange={(e) => { setQuery(e.target.value); setOpenDrop(true); }}
                onFocus={() => setOpenDrop(true)} data-testid="part-reassign-job-search" />
            </div>
            {openDrop && query.length >= 1 && (
              <div className="card" style={{ position: "absolute", zIndex: 20, top: 62, left: 0, right: 0, maxHeight: 260, overflowY: "auto", padding: 4 }}>
                {searching && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
                {!searching && jobResults.length === 0 && (
                  <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>No matching jobs for &quot;{query}&quot;.</div>
                )}
                {jobResults.map((r) => (
                  <button key={r.id} type="button" className="nav-item"
                    style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px" }}
                    onClick={() => pickJob(r)} data-testid={`part-reassign-job-opt-${r.id}`}>
                    <span className="mono t-strong" style={{ fontSize: 13 }}>{r.delivery_note_number}</span>
                    <span className="t-mut" style={{ fontSize: 11.5, marginLeft: 8 }}>{r.customer_name}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {job && (
        <div className="fld">
          <label>Equipment line <span className="req">*</span></label>
          <select className="input" value={itemId} onChange={(e) => setItemId(e.target.value)}
            data-testid="part-reassign-item-select">
            <option value="">— select a line —</option>
            {(job.items || []).map((it) => (
              <option key={it.id} value={it.id}>
                {it.description}{it.serial_number ? ` · SN ${it.serial_number}` : ""} · {it.status}
              </option>
            ))}
          </select>
        </div>
      )}
    </Modal>
  );
}

function PartDetail({ id, onClose, onChanged }) {
  const [part, setPart] = useState(null);
  const [step, setStep] = useState(null);
  const [editing, setEditing] = useState(false);
  const [reassigning, setReassigning] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/parts/${id}`); setPart(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!part) return <Modal open title="Loading…" onClose={onClose}><Loader /></Modal>;

  const steps0 = STATUS_STEPS[part.status] || [];
  const isTerminal = TERMINAL_PARTS_STATUSES.includes(part.status);
  const existingTos = new Set(steps0.map((s) => s.to));
  const steps = isTerminal ? [] : [...steps0, ...QUICK_CLOSE_ACTIONS.filter((s) => !existingTos.has(s.to))];
  const [label, cls] = PARTS_META[part.status] || [part.status, "b-slate"];

  return (
    <>
      <Modal open onClose={onClose} wide icon={Cog}
        title={part.part_description}
        subtitle={`${part.delivery_note_number || "—"} · ${part.customer_name || ""}`}
        footer={
          <>
            <button className="btn ghost" onClick={onClose}>Close</button>
            <button className="btn ghost" onClick={() => setReassigning(true)} data-testid="part-reassign-open">
              <ArrowRightLeft size={13} /> Reassign
            </button>
            <button className="btn ghost" onClick={() => setEditing(true)} data-testid="part-edit-open">
              <Pencil size={13} /> Edit
            </button>
            {steps.map((s) => (
              <button key={s.to} className={`btn ${s.tone === "danger" ? "ghost" : s.tone === "gold" ? "gold" : "ghost"}`}
                style={s.tone === "danger" ? { color: "var(--red)", borderColor: "#f3d6d4" } : {}}
                onClick={() => setStep(s)} data-testid={`part-step-${s.to}`}>
                <s.icon size={13} /> {s.label}
              </button>
            ))}
          </>
        }>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
          <Badge label={label} cls={cls} testid="parts-detail-status" />
          <span className="t-mut mono" style={{ fontSize: 12 }}>Created {fmtDateTime(part.created_at)} by {part.requested_by_name || "—"}</span>
        </div>

        <div className="form-grid" style={{ marginBottom: 14 }}>
          <div className="fld"><label>Part number</label><div className="mono">{part.part_number || "—"}</div></div>
          <div className="fld"><label>Quantity</label><div className="mono">{fmtNum(part.quantity || 1)}</div></div>
          <div className="fld"><label>Customer approval</label><div>{part.customer_approval_required ? "Required" : "Not required"}</div></div>
          <div className="fld"><label>Repair item?</label><div>{part.is_repair_item ? "Yes" : "No"}</div></div>
          <div className="fld"><label>Can work continue?</label><div>{part.can_work_continue ? "Yes" : "No"}</div></div>
          <div className="fld"><label>Technician</label><div>{part.technician_name || "—"}</div></div>
          <div className="fld" style={{ gridColumn: "span 2" }}>
            <label>Reason required</label>
            <div className="t-mut" style={{ whiteSpace: "pre-wrap" }}>{part.reason_required || "—"}</div>
          </div>
          <div className="fld"><label>Suggested supplier</label><div>{part.supplier_suggestion || "—"}</div></div>
          <div className="fld"><label>Supplier (chosen)</label><div className="t-strong">{part.supplier || "—"}</div></div>
          <div className="fld"><label>Quote #</label><div className="mono">{part.quote_number || "—"}</div></div>
          <div className="fld"><label>Purchase order</label><div className="mono">{part.purchase_order || "—"}</div></div>
          <div className="fld"><label>Cost (excl. VAT)</label><div className="mono">{money(part.cost)}</div></div>
          <div className="fld"><label>Expected ETA</label><div className="mono">{fmtDate(part.expected_eta)}</div></div>
          {part.received_at && <div className="fld"><label>Received</label><div className="mono">{fmtDateTime(part.received_at)} by {part.received_by_name || "—"}</div></div>}
          {part.notes && (
            <div className="fld" style={{ gridColumn: "span 2" }}>
              <label>Tech notes for parts/admin</label>
              <div className="t-mut" style={{ whiteSpace: "pre-wrap" }}>{part.notes}</div>
            </div>
          )}
        </div>

        <div className="sec-h"><h3 style={{ fontSize: 13 }}>Timeline</h3></div>
        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {(part.status_history || []).map((h, i) => (
            <li key={`${h.at}-${i}`} style={{ display: "flex", gap: 8, fontSize: 12 }}>
              <Clock size={11} style={{ marginTop: 4, color: "var(--text-3)" }} />
              <div>
                <span className="t-strong">{h.status}</span>
                {h.from_status && <span className="t-mut"> from {h.from_status}</span>}
                <span className="t-mut"> · {fmtDateTime(h.at)} · {h.by_name || "—"}</span>
                {h.note && <div className="t-mut" style={{ fontStyle: "italic" }}>“{h.note}”</div>}
              </div>
            </li>
          ))}
          {(part.status_history || []).length === 0 && <li className="t-mut" style={{ fontSize: 12 }}>No history.</li>}
        </ol>
      </Modal>

      {step && (
        <StepModal part={part} step={step}
          onClose={() => setStep(null)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
      {editing && (
        <EditPartModal part={part} onClose={() => setEditing(false)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
      {reassigning && (
        <ReassignPartModal part={part} onClose={() => setReassigning(false)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
    </>
  );
}

export default function Parts() {
  const { user } = useAuth();
  const mine = user?.role === "technician";
  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(null);
  const [section, setSection] = useState("new_requests");
  const [allRows, setAllRows] = useState([]);
  const [quickAction, setQuickAction] = useState(null);  // { part, step }

  const def = SECTIONS.find((s) => s.key === section) || SECTIONS[0];
  const filterRows = useMemo(() => (rows) => rows.filter(def.match), [def]);
  const counts = useMemo(() => {
    const c = {};
    SECTIONS.forEach((s) => { c[s.key] = allRows.filter(s.match).length; });
    return c;
  }, [allRows]);

  const columns = [
    { key: "delivery_note_number", label: "DN", sortable: true, mono: true, render: (v) => <span className="t-strong mono">{v || "—"}</span> },
    {
      key: "part_description", label: "Part", sortable: true,
      render: (v, row) => (
        <button onClick={() => setOpen(row.id)} data-testid={`part-open-${row.id}`}
          style={{ background: "none", border: 0, color: "var(--gold-deep)", cursor: "pointer", padding: 0, font: "inherit", fontWeight: 700, textAlign: "left" }}>
          {v}
          {row.part_number && <span className="mono t-mut" style={{ fontWeight: 400, fontSize: 11 }}> · {row.part_number}</span>}
        </button>
      ),
    },
    { key: "equipment_description", label: "Equipment", sortable: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "customer_name", label: "Customer", sortable: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "technician_name", label: "Requested by", sortable: true, render: (v, row) => <span className="t-mut">{v || row.requested_by_name || "—"}</span> },
    { key: "supplier", label: "Supplier", render: (v) => v || "—" },
    {
      key: "_flags", label: "Flags",
      render: (_, row) => (
        <span style={{ display: "inline-flex", gap: 4 }}>
          {row.customer_approval_required && <B label="Cust. approval" cls="b-amber" />}
          {row.is_repair_item && <B label="Repair" cls="b-violet" />}
          {row.can_work_continue && <B label="Non-blocking" cls="b-slate" />}
        </span>
      ),
    },
    {
      key: "status", label: "Status", sortable: true,
      render: (v) => { const [l, c] = PARTS_META[v] || [v, "b-slate"]; return <Badge label={l} cls={c} />; },
    },
    {
      key: "id", label: "Action", render: (_, row) => (
        <PartRowAction row={row} onView={() => setOpen(row.id)}
          onQuickAction={(step) => setQuickAction({ part: row, step })} />
      ),
    },
  ];

  const tabs = SECTIONS.map((s) => (
    <button key={s.key} className={`ftab ${section === s.key ? "active" : ""}`} onClick={() => setSection(s.key)} data-testid={`parts-section-${s.key}`}>
      {s.label} <span className="c">{fmtNum(counts[s.key] ?? 0)}</span>
    </button>
  ));

  return (
    <>
      <PageHead crumb="Workflow" title={mine ? "My Parts Requests" : "Parts Work Queue"}
        sub="Capture once. Once parts are physically in hand, pick Return to Tech, Move to Dispatch or RTC directly — no need to step through Quoted/Ordered/Received first." />

      <DataTable endpoint="/parts" params={mine ? { mine: true } : {}} columns={columns} rowKey="id" testid="parts" clientSide
        defaultSort={{ by: "created_at", dir: "desc" }} searchPlaceholder="Search part, supplier, DN, customer…"
        refreshSignal={refresh}
        filterRows={filterRows}
        onLoaded={(rows) => setAllRows(rows)}
        toolbar={tabs}
        emptyText="No parts requests in this section." />

      <div className="notice" style={{ marginTop: 18 }}>
        <Info size={16} />
        <span>
          <b>Return to Tech</b> puts the job-line straight back into the technician's workflow.
          <b> Move to Dispatch</b> sends the equipment directly to Dispatch (no further lab work needed).
          <b> RTC</b> returns the item to the customer with no work done and no charge.
        </span>
      </div>

      {open && <PartDetail id={open} onClose={() => setOpen(null)} onChanged={() => setRefresh((x) => x + 1)} />}
      {quickAction && (
        <StepModal part={quickAction.part} step={quickAction.step}
          onClose={() => setQuickAction(null)}
          onDone={() => setRefresh((x) => x + 1)} />
      )}
    </>
  );
}

// Per-row "Select action…" + Go — the fast path for closing a part request
// straight from the list, without opening the full detail modal. Only the 3
// quick-close outcomes are offered here (Reassign/Edit/granular quote-order
// tracking stay inside View, for the less common cases that need them).
function PartRowAction({ row, onView, onQuickAction }) {
  const [sel, setSel] = useState("");
  const isTerminal = TERMINAL_PARTS_STATUSES.includes(row.status);
  if (isTerminal) {
    return (
      <button className="btn sm ghost" onClick={onView} data-testid={`part-view-${row.id}`}><Eye size={12} /> View</button>
    );
  }
  const go = () => {
    if (!sel) { onView(); return; }
    const step = QUICK_CLOSE_ACTIONS.find((s) => s.to === sel);
    if (step) onQuickAction(step);
    setSel("");
  };
  return (
    <div className="line-action-control">
      <select className="line-action-select" value={sel} onChange={(e) => setSel(e.target.value)}
        data-testid={`part-action-select-${row.id}`}>
        <option value="">View</option>
        {QUICK_CLOSE_ACTIONS.map((s) => <option key={s.to} value={s.to}>{s.label}</option>)}
      </select>
      <button className="btn sm gold" onClick={go} data-testid={`part-action-go-${row.id}`}>Go</button>
    </div>
  );
}
